import { useMemo, useState } from "react";
import { VestigingFilter } from "../components/VestigingFilter";
import { BtwKeuze, useBtwCodes } from "../components/BtwKeuze";
import { useVestigingKeuze, type VestigingKeuze } from "../components/useVestigingKeuze";
import { useAiBeschikbaar } from "../components/AiTariefHulp";
import { SoortTegels, soortTegelsUitRijen } from "../components/CatalogusLijst";
import { speciesIconSrc } from "../components/AnimalOverview";
import { House } from "lucide-react";
import { OverviewHeader } from "../components/OverviewHeader";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import {
  PencilIcon,
  Button,
  CheckboxField,
  Field,
  Modal,
  SelectField,
  StatusBadge,
  TextareaField,
  type Column,
  PlusIcon,
  RotateRightIcon,
} from "@fawkes/ui";

import { formatDate, formatEuro, todayIso } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// verblijf-wizard-tarief Epic 2 (Story 2.1–2.4): the Asiel tarief overview — the React port of the Jmix
// `AsielTariefOverviewView`. Three top tabs (Hond & Kat / Overige diersoorten / Retour Eigenaar). This
// slice implements the **Hond & Kat** rule-engine tab fully: a grid of active rules + toolbar
// (Regel toevoegen / Laat AI je helpen / Test tarief / Parallelle run) with row actions
// (Bewerken / Deactiveren), the rule dialog (conditions builder + resultaat + berekening), the test
// (dry-run) dialog with its candidate-scoring trace, and the AI dialog. The other two tabs render a
// "volgt" placeholder — they are a separate follow-up slice.
//
// All endpoints use RELATIVE urls (the dataProvider prepends /api once; a leading slash doubles to
// /api/api). ROLE_TARIEF_EDIT is server-enforced — there is no client signal, so affordances stay
// enabled and a 403 surfaces inline. The "Met AI" button is hidden when /ai-suggestie 404s (the
// `fawkes.producten.ai-features.enabled` flag is off → the route does not exist).

// ── condition vocabulary (stable enum ids — wire format, NOT the Java enum NAMES) ──────────────────
// NB: the WIRE id differs from the enum constant for several attributes/operators (see
// ConditieAttribuut / ConditieOperator on the api side): PROCES→VERTREK_TYPE, LEEFTIJD_MAANDEN→LEEFTIJD,
// GEWICHT_KG→GEWICHT; LESS_THAN→LT, GREATER_THAN→GT. We send the wire ids verbatim.
const ATTRIBUTES = [
  "DIERSOORT",
  "DIERSOORT_GROEP",
  "GESLACHT",
  "VERTREK_TYPE",
  "LEEFTIJD",
  "GEWICHT",
  "GECASTREERD",
  "CHIP",
] as const;
const OPERATORS = ["EQUALS", "IN", "LT", "GT", "BETWEEN"] as const;

const CALC_METHODS = ["FIXED_AMOUNT", "PER_DAY", "BASE_PLUS_DISTANCE", "SURCHARGE", "NONE"] as const;
const PRICE_MODES = ["FIXED", "FROM_AMOUNT", "FREE", "MANUAL_QUOTE", "IN_CONSULTATION"] as const;
const DAY_COUNTS = ["INCLUSIVE_BOTH", "INCLUSIVE_FIRST", "PER_NIGHT"] as const;
const BILL_PERS = ["DAY", "NIGHT"] as const;
const ROUNDINGS = ["GEEN", "CENTEN_HALF_UP"] as const;

// ── wire DTOs ──────────────────────────────────────────────────────────────────────────────────────
interface ConditieDto {
  attribuut: string;
  operator: string;
  waarde: string | null;
}
interface ConflictDto {
  anderRegelId: number | null;
  anderRegelNaam: string;
  reden: string;
}
interface RegelDto {
  id: number;
  version: number;
  naam: string;
  vestigingId: number | null;
  vestigingSpecifiek: boolean;
  conditieOperator: string;
  condities: ConditieDto[];
  resultaatProductId: number | null;
  resultaatProductNaam: string | null;
  resultaatLabel: string | null;
  prioriteit: number;
  alleMatchesToepassen: boolean;
  calculationMethod: string | null;
  priceMode: string | null;
  eigenBedrag: number | null;
  dagBedrag: number | null;
  bedragPerKm: number | null;
  minDagen: number | null;
  dagTelling: string | null;
  factureerPer: string | null;
  afronding: string | null;
  btwCodeId: number | null;
  bronAiTekst: string | null;
  actief: boolean;
  waarschuwingen: ConflictDto[];
}

interface AiDraftDto {
  suggestieNaam: string;
  resultaatLabel: string;
  conditieOperator: string;
  condities: ConditieDto[];
  diersoort: string | null;
  vertrekType: string | null;
  leeftijdLt: number | null;
  chip: boolean;
  bedrag: string | null;
  bronAiTekst: string;
  previewTekst: string;
}

interface OptiesDto {
  btwCodes: { id: number; label: string }[];
}
interface ProductRow {
  id: number;
  naam: string;
}
interface ProductPage {
  content: ProductRow[];
}

// ── Overige diersoorten (DiersoortTariefknoop tree) wire DTOs ─────────────────────────────────────────
// Field names mirror DiersoortTariefknoopResponse exactly: own `bedrag*` + inheritance-walked
// `effectiefBedrag*`. parentKnoopId lets the client rebuild the hierarchy; positie orders siblings.
interface KnoopDto {
  id: number;
  version: number;
  naam: string;
  parentKnoopId: number | null;
  diersoortId: number | null;
  diersoortNaam: string | null;
  vestigingId: number | null;
  bedragPlaatsing: number | null;
  bedragAfstand: number | null;
  bedragAdoptie: number | null;
  ervenVanParent: boolean;
  positie: number;
  actief: boolean;
  effectiefBedragPlaatsing: number | null;
  effectiefBedragAfstand: number | null;
  effectiefBedragAdoptie: number | null;
}

// ── Retour eigenaar wire DTOs (RetourEigenaarRowResponse / bulk-create) ───────────────────────────────
interface RetourRowDto {
  tariefId: number | null;
  version: number | null;
  diersoortId: number | null;
  diersoortNaam: string;
  vestigingId: number | null;
  dagprijs: number | null;
  geldigVanaf: string | null;
  nieuw: boolean;
}
interface BulkCreateResultDto {
  aangemaakt: number;
  overgeslagen: number;
  overgeslagenDiersoorten: string[];
}
// The species selector feed reused for the bulk-create dialog (intake reference feed → {id,label,color}).
interface DiersoortOptie {
  id: number;
  label: string;
}

type SaveNotice = "conflict" | "forbidden" | "error" | null;
const statusToErr = (err: unknown): SaveNotice => {
  const code = (err as { statusCode?: number }).statusCode;
  return code === 409 ? "conflict" : code === 403 ? "forbidden" : "error";
};
const num = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};
const intOrNull = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
};

// ── the page ─────────────────────────────────────────────────────────────────────────────────────
type Tab = "hondkat" | "overige" | "retour";

export function AsielTariefOverview() {
  const t = useTranslate();
  const [tab, setTab] = useState<Tab>("hondkat");
  // Het bereik waarover je kijkt: alles, alleen wat organisatiebreed geldt, of alleen wat een
  // vestiging zelf heeft ingesteld. Client-side, want de rijen dragen hun vestiging al mee.
  // Dezelfde vestigingkeuze als op Producten & diensten en het factuuroverzicht.
  const vestiging = useVestigingKeuze();

  const tabs: { key: Tab; label: string }[] = [
    { key: "hondkat", label: t("asielTarief.tab.hondkat") },
    { key: "overige", label: t("asielTarief.tab.overige") },
    { key: "retour", label: t("asielTarief.tab.retour") },
  ];

  return (
    <div className="product-overview asiel-tarief">
      <OverviewHeader
        icon={<House size={24} />}
        title={t("menu.asielTarief")}
      />
      <div className="product-tabs" role="tablist" aria-label={t("asielTarief.tabs.label")}>
        {tabs.map((tabDef) => (
          <button
            key={tabDef.key}
            type="button"
            role="tab"
            aria-selected={tab === tabDef.key}
            className={`product-tab${tab === tabDef.key ? " product-tab--active" : ""}`}
            onClick={() => setTab(tabDef.key)}
          >
            {tabDef.label}
          </button>
        ))}
      </div>

      {tab === "hondkat" && <HondKatTab vestiging={vestiging} />}
      {tab === "overige" && <OverigeDiersoortenTab hoortErbij={vestiging.hoortErbij} />}
      {tab === "retour" && <RetourEigenaarTab hoortErbij={vestiging.hoortErbij} />}
    </div>
  );
}

// ── Hond & Kat rule-engine tab ─────────────────────────────────────────────────────────────────────
/**
 * De diersoort waar een tariefregel over gaat. Die staat in de voorwaarden ("Diersoort is gelijk
 * aan Hond"), niet in een eigen kolom — het is per slot een regelmotor en niet een tarieftabel.
 * Geen zo'n voorwaarde betekent: geldt voor elke soort.
 */
function diersoortVanRegel(regel: RegelDto): string | null {
  return regel.condities.find((c) => c.attribuut === "DIERSOORT")?.waarde ?? null;
}

function HondKatTab({ vestiging }: { vestiging: VestigingKeuze }) {
  const { hoortErbij } = vestiging;
  const t = useTranslate();

  const { data, isLoading, isError, refetch } = useCustom<RegelDto[]>({
    url: "asiel-tarief/regels",
    method: "get",
  });
  const { data: optiesData } = useCustom<OptiesDto>({ url: "producten/opties", method: "get" });
  const { data: productData } = useCustom<ProductPage>({
    url: "producten?scope=BEIDE&size=500&sort=naam,asc",
    method: "get",
  });

  // AI availability probe (FR / Story 2.4): the AI endpoint is gated by the
  // `fawkes.producten.ai-features.enabled` flag — with AI off the whole controller bean is absent and the
  // route does not exist, so ANY method (incl. this GET) gets a 404. With AI on the route exists but only
  // accepts POST, so a GET gets a 405. We therefore treat a *404 specifically* as "flag off → hide the
  // button", and anything else (405 / success) as "available". There is no client flag signal otherwise
  // (the api exposes no auth/config entry for it), so this status-probe is the faithful equivalent of the
  // Jmix dialog being hidden behind the same flag. retry:false so the 404 surfaces immediately.
  // Eén keer per sessie gevraagd en daarna vast, zodat de knop niet knippert (zie useAiBeschikbaar).
  const aiUnavailable = !useAiBeschikbaar("asiel-tarief/ai-suggestie");

  const allRules = useMemo(
    () => (Array.isArray(data?.data) ? data!.data : []).filter((r) => hoortErbij(r)),
    [data, hoortErbij],
  );
  const [zoek, setZoek] = useState("");
  const [soort, setSoort] = useState("");
  // The Hond & Kat tab shows only ACTIVE rules in its grid (deactivated rules drop out, like Jmix).
  const actieveRegels = useMemo(() => allRules.filter((r) => r.actief), [allRules]);

  const soortTegels = useMemo(() => soortTegelsUitRijen(
    actieveRegels,
    diersoortVanRegel,
    t("dierAanwezig.stat.total"),
    (naam) => <img src={speciesIconSrc(naam)} alt="" width={28} height={28} />,
  ), [actieveRegels, t]);

  const activeRules = useMemo(() => {
    const opSoort = soort
      ? actieveRegels.filter((r) => diersoortVanRegel(r) === soort)
      : actieveRegels;
    const q = zoek.trim().toLowerCase();
    return q
      ? opSoort.filter((r) =>
          r.naam.toLowerCase().includes(q) || (r.resultaatProductNaam ?? "").toLowerCase().includes(q))
      : opSoort;
  }, [actieveRegels, soort, zoek]);

  const btwCodes = optiesData?.data?.btwCodes ?? [];
  const products = productData?.data?.content ?? [];

  const [dialogRule, setDialogRule] = useState<RegelDto | "new" | null>(null);
  const [aiPrefill, setAiPrefill] = useState<Partial<RegelDto> | null>(null);
  const [aiOpen, setAiOpen] = useState(false);

  const { mutate: deactivate } = useCustomMutation();
  const [rowNotice, setRowNotice] = useState<SaveNotice>(null);

  const onDeactivate = (rule: RegelDto) => {
    setRowNotice(null);
    if (!globalThis.confirm?.(t("asielTarief.deactivate.confirm", { args: [rule.naam] }))) return;
    deactivate(
      {
        url: `asiel-tarief/regels/${rule.id}/deactiveren`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => refetch(),
        onError: (err) => setRowNotice(statusToErr(err)),
      },
    );
  };

  const summarizeConditions = (rule: RegelDto): string => {
    if (rule.condities.length === 0) return t("asielTarief.condition.any");
    const joiner = rule.conditieOperator === "OR" ? " OF " : " EN ";
    return rule.condities
      .map((c) => `${attrLabel(t, c.attribuut)} ${opLabel(t, c.operator)} ${c.waarde ?? ""}`.trim())
      .join(joiner);
  };

  const resultColumn = (rule: RegelDto): string =>
    rule.resultaatProductNaam ?? rule.resultaatLabel ?? t("asielTarief.result.none");

  const columns: Column<RegelDto>[] = [
    { header: t("asielTarief.col.naam"), cell: (r) => r.naam },
    { header: t("asielTarief.col.condities"), cell: summarizeConditions },
    { header: t("asielTarief.col.resultaat"), cell: resultColumn },
    { header: t("asielTarief.col.prioriteit"),
      align: "right",
      cell: (r) => String(r.prioriteit),
    },
    { header: t("asielTarief.col.acties"),
      cell: (r) => (
        <span className="asiel-tarief__row-actions">
          <button
            type="button"
            className="tarief-rijactie"
            onClick={() => setDialogRule(r)}
            title={t("common.edit")}
            aria-label={t("common.edit")}
          >
            <PencilIcon width={16} height={16} />
          </button>
          <button type="button" className="asiel-tarief__link" onClick={() => onDeactivate(r)}>
            {t("asielTarief.action.deactivate")}
          </button>
        </span>
      ),
    },
  ];

  return (
    <div className="asiel-tarief__panel">
      <div className="asiel-tarief__toolbar">
        <VestigingFilter
          label={t("financieel.filter.vestiging")}
          opties={vestiging.opties}
          gekozen={vestiging.gekozen}
          onChange={vestiging.setGekozen}
        />
        <Field
          label={t("asielTarief.search")}
          placeholder={t("asielTarief.search")}
          value={zoek}
          onChange={(e) => setZoek(e.target.value)}
        />
        {!aiUnavailable && (
          <Button variant="secondary" onClick={() => setAiOpen(true)}>
            {t("asielTarief.action.ai")}
          </Button>
        )}
        {/* "Test tarief" en "Parallelle run" stonden hier (Maarten, 2026-10-21: eruit). Het waren
            gereedschappen uit de migratie -- een tarief naspelen en legacy naast de tariefmotor
            leggen -- en die horen niet in de werkbalk van een asiel dat gewoon zijn tarieven beheert.
            De vensters zelf staan er nog; wil je ze terug, dan is het deze knop terugzetten. */}
        <div className="asiel-tarief__toolbar-knoppen">
          <button type="button" className="fawkes-products-vernieuw" onClick={() => refetch()}>
            <RotateRightIcon width={16} height={16} />
            {t("asielTarief.action.refresh")}
          </button>
          <Button
            onClick={() => {
              setAiPrefill(null);
              setDialogRule("new");
            }}
          >
            <PlusIcon width={16} height={16} /> {t("asielTarief.action.add")}
          </Button>
        </div>
      </div>

      {rowNotice && (
        <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
          {t(`asielTarief.notice.${rowNotice}`)}
        </div>
      )}

      {soortTegels.length > 0 && (
        <SoortTegels
          label={t("asielTarief.cond.attribuut")}
          gekozen={soort}
          onKies={setSoort}
          tegels={soortTegels}
        />
      )}

      {isError ? (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">
          {t("asielTarief.load.error")}
        </div>
      ) : (
        <IndeelbareTabel tabelKey="asiel-tarief-overview-1"
          columns={columns}
          rows={activeRules}
          getRowKey={(r) => String(r.id)}
          empty={isLoading ? t("common.loading") : t("asielTarief.empty")}
        />
      )}

      {dialogRule !== null && (
        <RegelDialog
          rule={dialogRule === "new" ? null : dialogRule}
          prefill={dialogRule === "new" ? aiPrefill : null}
          btwCodes={btwCodes}
          products={products}
          onClose={() => {
            setDialogRule(null);
            setAiPrefill(null);
          }}
          onSaved={() => {
            setDialogRule(null);
            setAiPrefill(null);
            refetch();
          }}
        />
      )}



      {aiOpen && (
        <AiDialog
          onClose={() => setAiOpen(false)}
          onAdopt={(draft) => {
            setAiOpen(false);
            setAiPrefill(draftToRule(draft));
            setDialogRule("new");
          }}
        />
      )}
    </div>
  );
}

// ── rule dialog (conditions builder + resultaat + berekening) ───────────────────────────────────────
interface CondRow {
  attribuut: string;
  operator: string;
  waarde: string;
}

function RegelDialog({
  rule,
  prefill,
  btwCodes,
  products,
  onClose,
  onSaved,
}: {
  rule: RegelDto | null;
  prefill: Partial<RegelDto> | null;
  btwCodes: { id: number; label: string }[];
  products: ProductRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslate();
  const seed = rule ?? prefill ?? null;

  const [naam, setNaam] = useState(seed?.naam ?? "");
  const [conditieOperator, setConditieOperator] = useState(seed?.conditieOperator ?? "AND");
  const [conds, setConds] = useState<CondRow[]>(
    (seed?.condities ?? []).map((c) => ({
      attribuut: c.attribuut,
      operator: c.operator,
      waarde: c.waarde ?? "",
    })),
  );
  const [resultaatProductId, setResultaatProductId] = useState<string>(
    seed?.resultaatProductId != null ? String(seed.resultaatProductId) : "",
  );
  const [resultaatLabel, setResultaatLabel] = useState(seed?.resultaatLabel ?? "");
  const [prioriteit, setPrioriteit] = useState(String(seed?.prioriteit ?? 0));
  const [alleMatches, setAlleMatches] = useState(seed?.alleMatchesToepassen ?? false);
  const [calculationMethod, setCalculationMethod] = useState(seed?.calculationMethod ?? "");
  const [priceMode, setPriceMode] = useState(seed?.priceMode ?? "");
  const [eigenBedrag, setEigenBedrag] = useState(seed?.eigenBedrag != null ? String(seed.eigenBedrag) : "");
  const [dagBedrag, setDagBedrag] = useState(seed?.dagBedrag != null ? String(seed.dagBedrag) : "");
  const [bedragPerKm, setBedragPerKm] = useState(seed?.bedragPerKm != null ? String(seed.bedragPerKm) : "");
  const [minDagen, setMinDagen] = useState(seed?.minDagen != null ? String(seed.minDagen) : "");
  const [dagTelling, setDagTelling] = useState(seed?.dagTelling ?? "");
  const [factureerPer, setFactureerPer] = useState(seed?.factureerPer ?? "");
  const [afronding, setAfronding] = useState(seed?.afronding ?? "");
  const [btwCodeId, setBtwCodeId] = useState<string>(seed?.btwCodeId != null ? String(seed.btwCodeId) : "");

  const [naamMissing, setNaamMissing] = useState(false);
  // BTW is verplicht op een regel: de motor zet dit percentage op de kostenregel wanneer het gekoppelde
  // product er zelf geen heeft, en zonder percentage viel de factuur stilletjes terug op 21%.
  const [btwMissing, setBtwMissing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  // The conflict warnings the API returns on a successful save (non-blocking — the save still happened).
  const [warnings, setWarnings] = useState<ConflictDto[]>([]);

  const { mutate, isLoading: saving } = useCustomMutation();

  const addCond = () =>
    setConds((c) => [...c, { attribuut: "DIERSOORT", operator: "EQUALS", waarde: "" }]);
  const removeCond = (idx: number) => setConds((c) => c.filter((_, i) => i !== idx));
  const setCond = (idx: number, patch: Partial<CondRow>) =>
    setConds((c) => c.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

  const submit = () => {
    const naamLeeg = naam.trim() === "";
    setNaamMissing(naamLeeg);
    setBtwMissing(btwCodeId === "");
    if (naamLeeg || btwCodeId === "") return;
    setNotice(null);

    // Conditions are serialized as a list of {attribuut, operator, waarde} triples using the stable
    // WIRE ids, plus the AND/OR chain operator. An empty list → a wildcard rule (matches anything).
    const condities = conds.map((c) => ({
      attribuut: c.attribuut,
      operator: c.operator,
      waarde: c.waarde.trim() === "" ? null : c.waarde.trim(),
    }));

    const values = {
      naam: naam.trim(),
      conditieOperator,
      condities,
      resultaatProductId: resultaatProductId === "" ? null : Number(resultaatProductId),
      resultaatLabel: resultaatLabel.trim() === "" ? null : resultaatLabel.trim(),
      prioriteit: intOrNull(prioriteit) ?? 0,
      alleMatchesToepassen: alleMatches,
      calculationMethod: calculationMethod || null,
      priceMode: priceMode || null,
      eigenBedrag: num(eigenBedrag),
      dagBedrag: num(dagBedrag),
      bedragPerKm: num(bedragPerKm),
      minDagen: intOrNull(minDagen),
      dagTelling: dagTelling || null,
      factureerPer: factureerPer || null,
      afronding: afronding || null,
      btwCodeId: Number(btwCodeId),
      bronAiTekst: seed?.bronAiTekst ?? null,
      ...(rule ? { version: rule.version } : {}),
    };

    mutate(
      {
        url: rule ? `asiel-tarief/regels/${rule.id}` : "asiel-tarief/regels",
        method: rule ? "put" : "post",
        values,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          const saved = res?.data as RegelDto | undefined;
          const w = saved?.waarschuwingen ?? [];
          if (w.length > 0) {
            // Surface the non-blocking conflict warnings, then close after the user has seen them.
            setWarnings(w);
          } else {
            onSaved();
          }
        },
        onError: (err) => setNotice(statusToErr(err)),
      },
    );
  };

  return (
    <Modal
      open
      // Wide: a condition is attribute + operator + value on one line, and at form width those three
      // selects push the value field out of the dialog (you could not read what you had typed).
      size="wide"
      title={rule ? t("asielTarief.dialog.edit.title") : t("asielTarief.dialog.new.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          {warnings.length > 0 ? (
            <Button onClick={onSaved}>{t("asielTarief.dialog.warnings.continue")}</Button>
          ) : (
            <Button onClick={submit} disabled={saving}>
              {t("common.save")}
            </Button>
          )}
        </>
      }
    >
      <div className="asiel-tarief__form">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
            {t(`asielTarief.notice.${notice}`)}
          </div>
        )}
        {warnings.length > 0 && (
          <div className="asiel-tarief__notice asiel-tarief__notice--warning" role="alert">
            <strong>{t("asielTarief.dialog.warnings.title")}</strong>
            <ul>
              {warnings.map((w, i) => (
                <li key={i}>
                  {w.anderRegelNaam}: {w.reden}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Voorwaarden (conditions builder) ── */}
        <fieldset className="asiel-tarief__section">
          <legend>{t("asielTarief.section.voorwaarden")}</legend>
          {/* Two per line: the extra width goes into shorter dialogs, not into stretched inputs. */}
          <div className="asiel-tarief__grid2">
            <div>
              <Field
                label={t("asielTarief.field.naam")}
                value={naam}
                onChange={(e) => setNaam(e.target.value)}
              />
              {naamMissing && (
                <div className="asiel-tarief__field-error">
                  {t("asielTarief.field.naam.required")}
                </div>
              )}
            </div>
            <SelectField
              label={t("asielTarief.field.conditieOperator")}
              value={conditieOperator}
              onChange={(e) => setConditieOperator(e.target.value)}
              options={[
                { value: "AND", label: t("asielTarief.operator.and") },
                { value: "OR", label: t("asielTarief.operator.or") },
              ]}
            />
          </div>

          <div className="asiel-tarief__conds">
            {conds.length === 0 && (
              <div className="asiel-tarief__cond-empty">{t("asielTarief.condition.any")}</div>
            )}
            {conds.map((c, idx) => (
              <div className="asiel-tarief__cond-row" key={idx} data-testid="cond-row">
                <SelectField
                  label={t("asielTarief.cond.attribuut")}
                  value={c.attribuut}
                  onChange={(e) => setCond(idx, { attribuut: e.target.value })}
                  options={ATTRIBUTES.map((a) => ({ value: a, label: attrLabel(t, a) }))}
                />
                <SelectField
                  label={t("asielTarief.cond.operator")}
                  value={c.operator}
                  onChange={(e) => setCond(idx, { operator: e.target.value })}
                  options={OPERATORS.map((o) => ({ value: o, label: opLabel(t, o) }))}
                />
                <Field
                  label={t("asielTarief.cond.waarde")}
                  value={c.waarde}
                  onChange={(e) => setCond(idx, { waarde: e.target.value })}
                />
                <button
                  type="button"
                  className="asiel-tarief__link asiel-tarief__cond-remove"
                  onClick={() => removeCond(idx)}
                  aria-label={t("asielTarief.cond.remove")}
                >
                  ×
                </button>
              </div>
            ))}
            <Button variant="secondary" onClick={addCond}>
              {t("asielTarief.cond.add")}
            </Button>
          </div>
        </fieldset>

        {/* ── Resultaat ── */}
        <fieldset className="asiel-tarief__section">
          <legend>{t("asielTarief.section.resultaat")}</legend>
          <div className="asiel-tarief__grid2">
            <SelectField
              label={t("asielTarief.field.resultaatProduct")}
              value={resultaatProductId}
              onChange={(e) => setResultaatProductId(e.target.value)}
              options={[
                { value: "", label: t("asielTarief.result.useLabel") },
                ...products.map((p) => ({ value: String(p.id), label: p.naam })),
              ]}
            />
            <Field
              label={t("asielTarief.field.resultaatLabel")}
              value={resultaatLabel}
              onChange={(e) => setResultaatLabel(e.target.value)}
            />
            <SelectField
              label={t("asielTarief.field.priceMode")}
              value={priceMode}
              onChange={(e) => setPriceMode(e.target.value)}
              options={[
                { value: "", label: t("asielTarief.option.none") },
                ...PRICE_MODES.map((m) => ({ value: m, label: t(`asielTarief.priceMode.${m}`) })),
              ]}
            />
          </div>
        </fieldset>

        {/* ── Berekening ── */}
        <fieldset className="asiel-tarief__section">
          <legend>{t("asielTarief.section.berekening")}</legend>
          <div className="asiel-tarief__grid2">
          <SelectField
            label={t("asielTarief.field.calculationMethod")}
            value={calculationMethod}
            onChange={(e) => setCalculationMethod(e.target.value)}
            options={[
              { value: "", label: t("asielTarief.option.none") },
              ...CALC_METHODS.map((m) => ({ value: m, label: t(`asielTarief.calc.${m}`) })),
            ]}
          />
          <Field
            label={t("asielTarief.field.eigenBedrag")}
            value={eigenBedrag}
            inputMode="decimal"
            onChange={(e) => setEigenBedrag(e.target.value)}
          />
          <Field
            label={t("asielTarief.field.dagBedrag")}
            value={dagBedrag}
            inputMode="decimal"
            onChange={(e) => setDagBedrag(e.target.value)}
          />
          <Field
            label={t("asielTarief.field.bedragPerKm")}
            value={bedragPerKm}
            inputMode="decimal"
            onChange={(e) => setBedragPerKm(e.target.value)}
          />
          <Field
            label={t("asielTarief.field.minDagen")}
            value={minDagen}
            inputMode="numeric"
            onChange={(e) => setMinDagen(e.target.value)}
          />
          <SelectField
            label={t("asielTarief.field.dagTelling")}
            value={dagTelling}
            onChange={(e) => setDagTelling(e.target.value)}
            options={[
              { value: "", label: t("asielTarief.option.none") },
              ...DAY_COUNTS.map((d) => ({ value: d, label: t(`asielTarief.dayCount.${d}`) })),
            ]}
          />
          <SelectField
            label={t("asielTarief.field.factureerPer")}
            value={factureerPer}
            onChange={(e) => setFactureerPer(e.target.value)}
            options={[
              { value: "", label: t("asielTarief.option.none") },
              ...BILL_PERS.map((b) => ({ value: b, label: t(`asielTarief.billPer.${b}`) })),
            ]}
          />
          <SelectField
            label={t("asielTarief.field.afronding")}
            value={afronding}
            onChange={(e) => setAfronding(e.target.value)}
            options={[
              { value: "", label: t("asielTarief.option.none") },
              ...ROUNDINGS.map((r) => ({ value: r, label: t(`asielTarief.rounding.${r}`) })),
            ]}
          />
          <BtwKeuze
            label={t("asielTarief.field.btwCode")}
            value={btwCodeId}
            onChange={(v) => { setBtwCodeId(v); setBtwMissing(false); }}
            codes={btwCodes}
            missing={btwMissing}
            t={t}
          />
          <Field
            label={t("asielTarief.field.prioriteit")}
            value={prioriteit}
            inputMode="numeric"
            onChange={(e) => setPrioriteit(e.target.value)}
          />
          <CheckboxField
            label={t("asielTarief.field.alleMatches")}
            checked={alleMatches}
            onChange={(e) => setAlleMatches(e.target.checked)}
          />
          </div>
        </fieldset>
      </div>
    </Modal>
  );
}



// ── AI dialog ───────────────────────────────────────────────────────────────────────────────────────
function AiDialog({
  onClose,
  onAdopt,
}: {
  onClose: () => void;
  onAdopt: (draft: AiDraftDto) => void;
}) {
  const t = useTranslate();
  const [tekst, setTekst] = useState("");
  const [draft, setDraft] = useState<AiDraftDto | null>(null);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const { mutate, isLoading } = useCustomMutation();

  const generate = () => {
    setNotice(null);
    mutate(
      {
        url: "asiel-tarief/ai-suggestie",
        method: "post",
        values: { tekst },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => setDraft(res?.data as AiDraftDto),
        onError: (err) => setNotice(statusToErr(err)),
      },
    );
  };

  return (
    <Modal
      open
      title={t("asielTarief.ai.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          {draft ? (
            <Button onClick={() => onAdopt(draft)}>{t("asielTarief.ai.adopt")}</Button>
          ) : (
            <Button onClick={generate} disabled={isLoading || tekst.trim() === ""}>
              {t("asielTarief.ai.generate")}
            </Button>
          )}
        </>
      }
    >
      <div className="asiel-tarief__form">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
            {t(`asielTarief.notice.${notice}`)}
          </div>
        )}
        <TextareaField
          label={t("asielTarief.ai.prompt")}
          value={tekst}
          rows={4}
          onChange={(e) => setTekst(e.target.value)}
        />
        {draft && (
          <div className="asiel-tarief__ai-preview" data-testid="ai-preview">
            <strong>{draft.suggestieNaam}</strong>
            <p>{draft.previewTekst}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Overige diersoorten tab (DiersoortTariefknoop tree) ──────────────────────────────────────────────
// The api returns a flat, positie-ordered list of nodes each carrying parentKnoopId. We rebuild the
// hierarchy client-side (children grouped under their parent, siblings kept in positie order) and render
// it depth-first with an indent per level. Each amount column shows the EFFECTIVE (inheritance-walked)
// value; when the node has no own `bedrag*` but an effective value exists, it's shown muted with a
// "(geërfd)" tag so inheritance is visible at a glance.

interface TreeNode {
  node: KnoopDto;
  depth: number;
}

function buildTree(flat: KnoopDto[]): TreeNode[] {
  const childrenByParent = new Map<number | null, KnoopDto[]>();
  for (const k of flat) {
    const key = k.parentKnoopId ?? null;
    const bucket = childrenByParent.get(key);
    if (bucket) bucket.push(k);
    else childrenByParent.set(key, [k]);
  }
  // Keep the api's positie order within each sibling group (stable; api already sorts by positie).
  for (const bucket of childrenByParent.values()) {
    bucket.sort((a, b) => a.positie - b.positie);
  }
  const knownIds = new Set(flat.map((k) => k.id));
  const out: TreeNode[] = [];
  const walk = (parentId: number | null, depth: number) => {
    for (const child of childrenByParent.get(parentId) ?? []) {
      out.push({ node: child, depth });
      walk(child.id, depth + 1);
    }
  };
  walk(null, 0);
  // Orphans: nodes whose parent is not in the loaded set (e.g. a parent outside the branch scope) —
  // render them at the top level so they are never silently dropped.
  for (const k of flat) {
    if (k.parentKnoopId != null && !knownIds.has(k.parentKnoopId) && !out.some((n) => n.node.id === k.id)) {
      out.push({ node: k, depth: 0 });
    }
  }
  return out;
}

function OverigeDiersoortenTab({ hoortErbij }: { hoortErbij: (rij: { vestigingId?: number | null }) => boolean }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError, refetch } = useCustom<KnoopDto[]>({
    url: "asiel-tarief/diersoort-knopen",
    method: "get",
  });

  const flat = useMemo(
    () => (Array.isArray(data?.data) ? data!.data : []).filter((k) => hoortErbij(k)),
    [data, hoortErbij],
  );
  const rows = useMemo(() => buildTree(flat), [flat]);

  const [dialogNode, setDialogNode] = useState<KnoopDto | "new" | null>(null);
  const { mutate: del } = useCustomMutation();
  const [rowNotice, setRowNotice] = useState<SaveNotice>(null);

  const onDelete = (n: KnoopDto) => {
    setRowNotice(null);
    if (!globalThis.confirm?.(t("asielTarief.overige.delete.confirm", { args: [n.naam] }))) return;
    del(
      {
        url: `asiel-tarief/diersoort-knopen/${n.id}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => refetch(),
        onError: (err) => setRowNotice(statusToErr(err)),
      },
    );
  };

  // An amount cell: prefers the node's OWN bedrag; falls back to the effective (inherited) value, shown
  // muted with a "(geërfd)" tag. "—" when neither exists.
  const amountCell = (own: number | null, effectief: number | null) => {
    if (own != null) return formatEuro(own, locale);
    if (effectief != null) {
      return (
        <span className="asiel-tarief__amount--inherited">
          {formatEuro(effectief, locale)}
          <span className="asiel-tarief__inherited-tag">{t("asielTarief.overige.inherited")}</span>
        </span>
      );
    }
    return t("asielTarief.overige.none");
  };

  const columns: Column<TreeNode>[] = [
    {
      header: t("asielTarief.overige.col.naam"),
      cell: ({ node, depth }) => (
        <span className="asiel-tarief__tree-name">
          <span className="asiel-tarief__tree-indent" style={{ width: `${depth * 20}px` }} />
          {node.naam}
        </span>
      ),
    },
    {
      header: t("asielTarief.overige.col.plaatsing"),
      align: "right",
      cell: ({ node }) => amountCell(node.bedragPlaatsing, node.effectiefBedragPlaatsing),
    },
    {
      header: t("asielTarief.overige.col.afstand"),
      align: "right",
      cell: ({ node }) => amountCell(node.bedragAfstand, node.effectiefBedragAfstand),
    },
    {
      header: t("asielTarief.overige.col.adoptie"),
      align: "right",
      cell: ({ node }) => amountCell(node.bedragAdoptie, node.effectiefBedragAdoptie),
    },
    {
      header: t("asielTarief.overige.col.acties"),
      cell: ({ node }) => (
        <span className="asiel-tarief__row-actions">
          <button
            type="button"
            className="tarief-rijactie"
            onClick={() => setDialogNode(node)}
            title={t("common.edit")}
            aria-label={t("common.edit")}
          >
            <PencilIcon width={16} height={16} />
          </button>
          <button type="button" className="asiel-tarief__link" onClick={() => onDelete(node)}>
            {t("asielTarief.action.deactivate")}
          </button>
        </span>
      ),
    },
  ];

  return (
    <div className="asiel-tarief__panel">
      <div className="asiel-tarief__toolbar">
        <Button onClick={() => setDialogNode("new")}>
          <PlusIcon width={16} height={16} /> {t("asielTarief.overige.add")}
        </Button>
        <span className="asiel-tarief__toolbar-spacer" />
        <Button variant="secondary" onClick={() => refetch()}>
          {t("asielTarief.action.refresh")}
        </Button>
      </div>

      {rowNotice && (
        <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
          {t(`asielTarief.notice.${rowNotice}`)}
        </div>
      )}

      {isError ? (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">
          {t("asielTarief.overige.load.error")}
        </div>
      ) : (
        <IndeelbareTabel tabelKey="asiel-tarief-overview-2"
          columns={columns}
          rows={rows}
          getRowKey={({ node }) => String(node.id)}
          empty={isLoading ? t("common.loading") : t("asielTarief.overige.empty")}
        />
      )}

      {/* The legacy AsielPrijsCategorie grid (Prijs wijzigen / Historie) is reconciliation scope (Epic
          6) — surfaced as a note only, no new price-change path here. */}
      <p className="asiel-tarief__legacy-note">{t("asielTarief.overige.legacyGrid.note")}</p>

      {dialogNode !== null && (
        <KnoopDialog
          node={dialogNode === "new" ? null : dialogNode}
          allNodes={flat}
          onClose={() => setDialogNode(null)}
          onSaved={() => {
            setDialogNode(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function KnoopDialog({
  node,
  allNodes,
  onClose,
  onSaved,
}: {
  node: KnoopDto | null;
  allNodes: KnoopDto[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslate();

  const [naam, setNaam] = useState(node?.naam ?? "");
  const [parentKnoopId, setParentKnoopId] = useState<string>(
    node?.parentKnoopId != null ? String(node.parentKnoopId) : "",
  );
  const [diersoortId, setDiersoortId] = useState<string>(
    node?.diersoortId != null ? String(node.diersoortId) : "",
  );
  const [bedragPlaatsing, setBedragPlaatsing] = useState(
    node?.bedragPlaatsing != null ? String(node.bedragPlaatsing) : "",
  );
  const [bedragAfstand, setBedragAfstand] = useState(
    node?.bedragAfstand != null ? String(node.bedragAfstand) : "",
  );
  const [bedragAdoptie, setBedragAdoptie] = useState(
    node?.bedragAdoptie != null ? String(node.bedragAdoptie) : "",
  );
  const [ervenVanParent, setErvenVanParent] = useState(node?.ervenVanParent ?? true);
  const [positie, setPositie] = useState(String(node?.positie ?? 0));

  const [naamMissing, setNaamMissing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const { mutate, isLoading: saving } = useCustomMutation();

  // A node cannot be its own parent; deeper cycle prevention is enforced server-side.
  const parentOptions = allNodes.filter((n) => n.id !== node?.id);

  const submit = () => {
    if (naam.trim() === "") {
      setNaamMissing(true);
      return;
    }
    setNaamMissing(false);
    setNotice(null);

    const values = {
      naam: naam.trim(),
      parentKnoopId: parentKnoopId === "" ? null : Number(parentKnoopId),
      diersoortId: diersoortId === "" ? null : Number(diersoortId),
      bedragPlaatsing: num(bedragPlaatsing),
      bedragAfstand: num(bedragAfstand),
      bedragAdoptie: num(bedragAdoptie),
      ervenVanParent,
      positie: intOrNull(positie) ?? 0,
      ...(node ? { version: node.version } : {}),
    };

    mutate(
      {
        url: node ? `asiel-tarief/diersoort-knopen/${node.id}` : "asiel-tarief/diersoort-knopen",
        method: node ? "put" : "post",
        values,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onSaved(),
        onError: (err) => setNotice(statusToErr(err)),
      },
    );
  };

  return (
    <Modal
      open
      title={
        node ? t("asielTarief.overige.dialog.edit.title") : t("asielTarief.overige.dialog.new.title")
      }
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="asiel-tarief__form">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
            {t(`asielTarief.notice.${notice}`)}
          </div>
        )}
        <Field
          label={t("asielTarief.overige.field.naam")}
          value={naam}
          onChange={(e) => setNaam(e.target.value)}
        />
        {naamMissing && (
          <div className="asiel-tarief__field-error">{t("asielTarief.overige.field.naam.required")}</div>
        )}
        <SelectField
          label={t("asielTarief.overige.field.parent")}
          value={parentKnoopId}
          onChange={(e) => setParentKnoopId(e.target.value)}
          options={[
            { value: "", label: t("asielTarief.overige.field.parent.none") },
            ...parentOptions.map((n) => ({ value: String(n.id), label: n.naam })),
          ]}
        />
        <Field
          label={t("asielTarief.overige.field.diersoortId")}
          value={diersoortId}
          inputMode="numeric"
          onChange={(e) => setDiersoortId(e.target.value)}
        />
        <Field
          label={t("asielTarief.overige.field.bedragPlaatsing")}
          value={bedragPlaatsing}
          inputMode="decimal"
          onChange={(e) => setBedragPlaatsing(e.target.value)}
        />
        <Field
          label={t("asielTarief.overige.field.bedragAfstand")}
          value={bedragAfstand}
          inputMode="decimal"
          onChange={(e) => setBedragAfstand(e.target.value)}
        />
        <Field
          label={t("asielTarief.overige.field.bedragAdoptie")}
          value={bedragAdoptie}
          inputMode="decimal"
          onChange={(e) => setBedragAdoptie(e.target.value)}
        />
        <CheckboxField
          label={t("asielTarief.overige.field.ervenVanParent")}
          checked={ervenVanParent}
          onChange={(e) => setErvenVanParent(e.target.checked)}
        />
        <Field
          label={t("asielTarief.overige.field.positie")}
          value={positie}
          inputMode="numeric"
          onChange={(e) => setPositie(e.target.value)}
        />
      </div>
    </Modal>
  );
}

// ── Retour Eigenaar tab (return-to-owner daily rates) ─────────────────────────────────────────────────
// Grid of one head per species with its active dagprijs + geldig-vanaf and a 30-day "Nieuw" badge
// (server-computed `nieuw`). The toolbar "Tarief toevoegen" opens a bulk dialog (multi-select species +
// one dagprijs + geldig-vanaf) that POSTs the bulk-create; the response reports how many were created and
// which species were skipped (already had an active head). Each row has a "Deactiveren" action. A
// back-dated effectdatum needs ROLE_TARIEF_ADMIN server-side → a 403 surfaces with a clear message.

function RetourEigenaarTab({ hoortErbij }: { hoortErbij: (rij: { vestigingId?: number | null }) => boolean }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError, refetch } = useCustom<RetourRowDto[]>({
    url: "asiel-tarief/retour-eigenaar",
    method: "get",
  });

  const rows = useMemo(
    () => (Array.isArray(data?.data) ? data!.data : []).filter((r) => hoortErbij(r)),
    [data, hoortErbij],
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const { mutate: deactivate } = useCustomMutation();
  const [rowNotice, setRowNotice] = useState<"forbidden" | "error" | null>(null);

  const onDeactivate = (r: RetourRowDto) => {
    setRowNotice(null);
    if (r.tariefId == null) return;
    if (!globalThis.confirm?.(t("asielTarief.retour.deactivate.confirm", { args: [r.diersoortNaam] })))
      return;
    deactivate(
      {
        url: `asiel-tarief/retour-eigenaar/${r.tariefId}/deactiveren`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => refetch(),
        onError: (err) =>
          setRowNotice(
            (err as { statusCode?: number }).statusCode === 403 ? "forbidden" : "error",
          ),
      },
    );
  };

  const columns: Column<RetourRowDto>[] = [
    { header: t("asielTarief.retour.col.diersoort"), cell: (r) => r.diersoortNaam },
    {
      header: t("asielTarief.retour.col.dagprijs"),
      align: "right",
      cell: (r) => (r.dagprijs != null ? formatEuro(r.dagprijs, locale) : t("asielTarief.retour.none")),
    },
    {
      header: t("asielTarief.retour.col.geldigVanaf"),
      cell: (r) => (
        <span className="asiel-tarief__nieuw-cell">
          {r.geldigVanaf ? formatDate(r.geldigVanaf, locale) : t("asielTarief.retour.none")}
          {r.nieuw && <StatusBadge tone="success">{t("asielTarief.retour.badge.nieuw")}</StatusBadge>}
        </span>
      ),
    },
    {
      header: t("asielTarief.retour.col.acties"),
      cell: (r) =>
        r.tariefId != null ? (
          <span className="asiel-tarief__row-actions">
            <button type="button" className="asiel-tarief__link" onClick={() => onDeactivate(r)}>
              {t("asielTarief.retour.deactivate")}
            </button>
          </span>
        ) : null,
    },
  ];

  return (
    <div className="asiel-tarief__panel">
      <div className="asiel-tarief__toolbar">
        <Button onClick={() => setDialogOpen(true)}>{t("asielTarief.retour.add")}</Button>
        <span className="asiel-tarief__toolbar-spacer" />
        <Button variant="secondary" onClick={() => refetch()}>
          {t("asielTarief.action.refresh")}
        </Button>
      </div>

      {rowNotice && (
        <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
          {t(`asielTarief.retour.notice.${rowNotice}`)}
        </div>
      )}

      {isError ? (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">
          {t("asielTarief.retour.load.error")}
        </div>
      ) : (
        <IndeelbareTabel tabelKey="asiel-tarief-overview-3"
          columns={columns}
          rows={rows}
          getRowKey={(r) => String(r.tariefId ?? r.diersoortId ?? r.diersoortNaam)}
          empty={isLoading ? t("common.loading") : t("asielTarief.retour.empty")}
        />
      )}

      {dialogOpen && (
        <RetourBulkDialog
          onClose={() => setDialogOpen(false)}
          onSaved={() => {
            setDialogOpen(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function RetourBulkDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const t = useTranslate();

  // Species options reused from the intake reference feed (the canonical species selector used by the
  // inboeken wizard) — there is no ROLE_TARIEF_EDIT-scoped diersoort feed, and the bulk-create accepts
  // raw diersoort ids. retry:false so an unreachable feed degrades gracefully.
  const { data: optiesData } = useCustom<DiersoortOptie[]>({
    url: "intake/reference/diersoorten",
    method: "get",
    queryOptions: { retry: false },
  });
  const opties = useMemo(
    () => (Array.isArray(optiesData?.data) ? optiesData!.data : []),
    [optiesData],
  );

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [dagprijs, setDagprijs] = useState("");
  const [effectdatum, setEffectdatum] = useState(todayIso());
  // Een nieuwe retour-eigenaarkop heeft geen eerdere versie om BTW van over te nemen: kiezen is verplicht.
  const btwCodes = useBtwCodes();
  const [btwCodeId, setBtwCodeId] = useState("");

  const [diersoortMissing, setDiersoortMissing] = useState(false);
  const [dagprijsMissing, setDagprijsMissing] = useState(false);
  const [btwMissing, setBtwMissing] = useState(false);
  // forbiddenPast = a back-dated effectdatum rejected for lack of ROLE_TARIEF_ADMIN (403).
  const [notice, setNotice] = useState<"forbiddenPast" | "forbidden" | "error" | null>(null);
  const [report, setReport] = useState<BulkCreateResultDto | null>(null);

  const { mutate, isLoading: saving } = useCustomMutation();

  const toggle = (id: number) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = () => {
    const ids = [...selected];
    const prijs = num(dagprijs);
    setDiersoortMissing(ids.length === 0);
    setDagprijsMissing(prijs == null);
    setBtwMissing(btwCodeId === "");
    if (ids.length === 0 || prijs == null || btwCodeId === "") return;
    setNotice(null);
    setReport(null);

    const isPast = effectdatum !== "" && effectdatum < todayIso();

    mutate(
      {
        url: "asiel-tarief/retour-eigenaar",
        method: "post",
        values: {
          diersoortIds: ids,
          dagprijs: prijs,
          effectdatum: effectdatum === "" ? null : effectdatum,
          btwCodeId: Number(btwCodeId),
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          const result = res?.data as BulkCreateResultDto | undefined;
          setReport(result ?? { aangemaakt: ids.length, overgeslagen: 0, overgeslagenDiersoorten: [] });
        },
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          // A 403 on a back-dated request is the admin-gate; a 403 otherwise = no edit rights.
          setNotice(code === 403 ? (isPast ? "forbiddenPast" : "forbidden") : "error");
        },
      },
    );
  };

  return (
    <Modal
      open
      title={t("asielTarief.retour.dialog.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {report ? t("common.close") : t("common.cancel")}
          </Button>
          {report ? (
            <Button onClick={onSaved}>{t("common.close")}</Button>
          ) : (
            <Button onClick={submit} disabled={saving}>
              {t("asielTarief.retour.create")}
            </Button>
          )}
        </>
      }
    >
      <div className="asiel-tarief__form">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error" role="alert">
            {t(`asielTarief.retour.notice.${notice}`)}
          </div>
        )}

        {report ? (
          <div className="asiel-tarief__notice asiel-tarief__notice--success" data-testid="retour-report">
            <div>{t("asielTarief.retour.report.created", { args: [String(report.aangemaakt)] })}</div>
            {report.overgeslagen > 0 && (
              <div>
                {t("asielTarief.retour.report.skipped", {
                  args: [report.overgeslagenDiersoorten.join(", ")],
                })}
              </div>
            )}
          </div>
        ) : (
          <>
            <label className="fui-field__label">{t("asielTarief.retour.field.diersoorten")}</label>
            <div className="asiel-tarief__diersoort-picker" role="group">
              {opties.map((o) => (
                <CheckboxField
                  key={o.id}
                  label={o.label}
                  checked={selected.has(o.id)}
                  onChange={() => toggle(o.id)}
                />
              ))}
            </div>
            {diersoortMissing && (
              <div className="asiel-tarief__field-error">
                {t("asielTarief.retour.field.diersoorten.required")}
              </div>
            )}
            <Field
              label={t("asielTarief.retour.field.dagprijs")}
              value={dagprijs}
              inputMode="decimal"
              onChange={(e) => setDagprijs(e.target.value)}
            />
            {dagprijsMissing && (
              <div className="asiel-tarief__field-error">
                {t("asielTarief.retour.field.dagprijs.required")}
              </div>
            )}
            <Field
              label={t("asielTarief.retour.field.effectdatum")}
              type="date"
              value={effectdatum}
              onChange={(e) => setEffectdatum(e.target.value)}
            />
            <BtwKeuze
              value={btwCodeId}
              onChange={(v) => { setBtwCodeId(v); setBtwMissing(false); }}
              codes={btwCodes}
              missing={btwMissing}
              t={t}
            />
          </>
        )}
      </div>
    </Modal>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────────────────────────────
function draftToRule(draft: AiDraftDto): Partial<RegelDto> {
  return {
    naam: draft.suggestieNaam,
    conditieOperator: draft.conditieOperator,
    condities: draft.condities,
    resultaatLabel: draft.resultaatLabel,
    bronAiTekst: draft.bronAiTekst,
  };
}

function attrLabel(t: ReturnType<typeof useTranslate>, id: string): string {
  return t(`asielTarief.attr.${id}`, undefined, id);
}
function opLabel(t: ReturnType<typeof useTranslate>, id: string): string {
  return t(`asielTarief.op.${id}`, undefined, id);
}
